import React, { useEffect, useState } from 'react';
import { X, User, Mail, Phone, MapPin, Calendar, Briefcase, GraduationCap, Instagram, Wallet, Award, Users } from 'lucide-react';
import { UserDocument } from '../../types';
import { apiService } from '../../services/api';
import { UserProfileSkeleton } from './Skeleton';
import toast from 'react-hot-toast';

interface UserProfileProps {
  isOpen: boolean;
  onClose: () => void;
}

export const UserProfile: React.FC<UserProfileProps> = ({ isOpen, onClose }) => {
  const [userProfile, setUserProfile] = useState<UserDocument | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadUserProfile();
    }
  }, [isOpen]);

  const loadUserProfile = async () => {
    setIsLoading(true);
    try {
      const profile = await apiService.getUserInfo();
      setUserProfile(profile);
    } catch (error: any) {
      console.error('Failed to load user profile:', error);
      toast.error(error.message || 'Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString();
  };

  const formatWallet = (paisa: number) => {
    return `₹${(paisa / 100).toFixed(2)}`;
  };

  const getGenderText = (gender: number) => {
    switch (gender) {
      case 1: return 'Male';
      case 2: return 'Female';
      case 3: return 'Other';
      default: return 'Not specified';
    }
  };

  const getMaritalStatusText = (status: number) => {
    switch (status) {
      case 1: return 'Single';
      case 2: return 'Married';
      case 3: return 'Divorced';
      case 4: return 'Widowed';
      default: return 'Not specified';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fade-in" role="dialog" aria-modal="true" aria-labelledby="profile-dialog-title">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 id="profile-dialog-title" className="text-lg font-semibold text-gray-900 dark:text-white">Profile Details</h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[70vh] p-4">
          {isLoading ? (
            <UserProfileSkeleton />
          ) : userProfile ? (
            <div className="space-y-6">
              {/* Profile Header */}
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center">
                  {userProfile.profilePic1 ? (
                    <img
                      src={userProfile.profilePic1}
                      alt={userProfile.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                  ) : (
                    <User className="h-8 w-8 text-primary-600" />
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{userProfile.name}</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{userProfile.role}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Member since {formatDate(userProfile.memberSince)}</p>
                </div>
              </div>

              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <h3 className="font-medium text-gray-900 dark:text-white flex items-center">
                    <Phone className="h-4 w-4 mr-2" />
                    Contact Information
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Phone:</span>
                      <span className="ml-2 font-medium">{userProfile.phoneNumber}</span>
                    </div>
                    {userProfile.email && (
                      <div className="flex items-center">
                        <Mail className="h-4 w-4 mr-2 text-gray-400" />
                        <span>{userProfile.email}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className="font-medium text-gray-900 dark:text-white flex items-center">
                    <MapPin className="h-4 w-4 mr-2" />
                    Location
                  </h3>
                  <div className="space-y-2 text-sm">
                    {userProfile.currentLocation && (
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Current:</span>
                        <span className="ml-2">{userProfile.currentLocation}</span>
                      </div>
                    )}
                    {userProfile.homeState && (
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Home State:</span>
                        <span className="ml-2">{userProfile.homeState}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <h3 className="font-medium text-gray-900 dark:text-white flex items-center">
                    <User className="h-4 w-4 mr-2" />
                    Personal Details
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Gender:</span>
                      <span className="ml-2">{getGenderText(userProfile.gender)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Marital Status:</span>
                      <span className="ml-2">{getMaritalStatusText(userProfile.maritalStatus)}</span>
                    </div>
                    {userProfile.dob && (
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                        <span>{formatDate(parseInt(userProfile.dob))}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className="font-medium text-gray-900 dark:text-white flex items-center">
                    <Briefcase className="h-4 w-4 mr-2" />
                    Professional
                  </h3>
                  <div className="space-y-2 text-sm">
                    {userProfile.jobTitle && (
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Job Title:</span>
                        <span className="ml-2">{userProfile.jobTitle}</span>
                      </div>
                    )}
                    {userProfile.jobCompany && (
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Company:</span>
                        <span className="ml-2">{userProfile.jobCompany}</span>
                      </div>
                    )}
                    {userProfile.college && (
                      <div className="flex items-center">
                        <GraduationCap className="h-4 w-4 mr-2 text-gray-400" />
                        <span>{userProfile.college}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Trip Statistics */}
              <div className="space-y-3">
                <h3 className="font-medium text-gray-900 dark:text-white flex items-center">
                  <Award className="h-4 w-4 mr-2" />
                  Trip Statistics
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg text-center">
                    <div className="text-2xl font-bold text-primary-600">{userProfile.numberOfSuccessfulTrips}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Successful Trips</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg text-center">
                    <div className="text-2xl font-bold text-primary-600">{userProfile.tripCount}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Total Trips</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg text-center">
                    <div className="text-2xl font-bold text-primary-600">{userProfile.meetupCount}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Meetups</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg text-center">
                    <div className="text-2xl font-bold text-primary-600">{userProfile.coordinator ? 'Yes' : 'No'}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Coordinator</div>
                  </div>
                </div>
              </div>

              {/* Wallet */}
              <div className="space-y-3">
                <h3 className="font-medium text-gray-900 dark:text-white flex items-center">
                  <Wallet className="h-4 w-4 mr-2" />
                  Wallet Balance
                </h3>
                <div className="bg-green-50 p-3 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{formatWallet(userProfile.wallet)}</div>
                  <div className="text-sm text-green-700">Available Balance</div>
                </div>
              </div>

              {/* Social Links */}
              {userProfile.instagramUrl && (
                <div className="space-y-3">
                  <h3 className="font-medium text-gray-900 dark:text-white flex items-center">
                    <Instagram className="h-4 w-4 mr-2" />
                    Social Media
                  </h3>
                  <div className="flex items-center space-x-2">
                    <Instagram className="h-4 w-4 text-pink-500" />
                    <a
                      href={userProfile.instagramUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      {userProfile.instagramUrl}
                    </a>
                    {userProfile.instagramVerified && (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                        Verified
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Additional Info */}
              {(userProfile.aboutMe || userProfile.interests || userProfile.languageKnown) && (
                <div className="space-y-3">
                  <h3 className="font-medium text-gray-900 dark:text-white">Additional Information</h3>
                  <div className="space-y-2 text-sm">
                    {userProfile.aboutMe && (
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">About:</span>
                        <p className="mt-1 text-gray-800 dark:text-gray-200">{userProfile.aboutMe}</p>
                      </div>
                    )}
                    {userProfile.interests && (
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Interests:</span>
                        <p className="mt-1 text-gray-800 dark:text-gray-200">{userProfile.interests}</p>
                      </div>
                    )}
                    {userProfile.languageKnown && (
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Languages:</span>
                        <span className="ml-2">{userProfile.languageKnown}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400 mb-3">Failed to load profile</p>
              <button
                onClick={loadUserProfile}
                className="text-sm text-primary-600 hover:text-primary-500 font-medium underline"
              >
                Retry
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
